import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { CounterDto, RequestDto } from '@78/shared';
import { refundPercentOf } from '@78/core';
import {
  acceptCounter,
  cancelRequest,
  declineCounter,
  fetchMyCounters,
  fetchMyRequests,
  fetchRegistry,
  fetchWalletDeals,
} from '../lib/api.ts';
import { fetchOwnedNfts, getNftOwner } from '../lib/chain.ts';
import { GiftArt } from '../components/GiftArt.tsx';
import { useActions } from '../lib/actions.ts';
import { getInitData } from '../lib/telegram.ts';
import { sameAddress, useCollectionNames, shortAddress } from '../lib/collections.ts';
import { useErrorText } from '../lib/errors.ts';
import { Money, useFmt } from '../lib/format.tsx';
import { Board } from './Board.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { reportFailure } from '../lib/report.ts';

/**
 * Вещь в разделе «Мои NFT» — своя, независимо от того, где она лежит.
 *
 * Свободная приходит из кошелька, заложенная — из сделки: кошелёк ею уже
 * не владеет, и в списке TON API её нет. Собираем оба источника в один
 * список, иначе выставленная вещь исчезает с экрана ровно в тот момент,
 * когда владельцу важнее всего видеть, что с ней.
 */
interface MineCard {
  /** Адрес самого предмета — он же ключ списка. */
  nft: string;
  name: string | null;
  image: string | null;
  collection: string | null;
  /** free — лежит в кошельке; awaiting_lock — оффер создан, вещь дома;
   *  locked — вещь в контракте, покупателя пока нет. */
  kind: 'free' | 'awaiting_lock' | 'locked';
  /** Адрес сделки для двух последних состояний. */
  deal?: string;
}

/**
 * Что делать с вещью — зависит от того, где она сейчас.
 *
 * Свободна: выставить. Оффер создан, но вещь дома: передать её в контракт.
 * Вещь в контракте, покупателя нет: ждать или отменить и забрать назад.
 * Всё это — один и тот же предмет глазами владельца, поэтому и живёт на
 * одной карточке: раньше на каждом шаге вещь исчезала из раздела и
 * появлялась в другом, а на последнем шаге пропадала совсем — кошелёк ею
 * уже не владел, и списку было неоткуда её взять.
 */
function CardAction({
  card,
  onList,
}: {
  card: MineCard;
  onList: () => void;
}) {
  const { t } = useTranslation();
  const actions = useActions();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['myDeals', actions.myAddress] });
    void qc.invalidateQueries({ queryKey: ['myNfts', actions.myAddress] });
  };

  const run = async (where: string, fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy(true);
    try {
      await fn();
      refresh();
    } catch (e) {
      reportFailure(where, e);
      const text = errorText(e);
      setErr(text.length > 0 ? text : null);
    } finally {
      setBusy(false);
    }
  };

  if (card.kind === 'free') {
    return (
      <button className="btn" style={{ padding: 9, fontSize: 11.5 }} onClick={onList}>
        {t('mine.list_it')}
      </button>
    );
  }

  if (card.kind === 'awaiting_lock') {
    return (
      <>
        <span className="dim" style={{ fontSize: 10.5, lineHeight: 1.3 }}>{t('mine.already_listed')}</span>
        <button
          className="btn"
          style={{ padding: 9, fontSize: 11.5 }}
          disabled={busy}
          onClick={() =>
            void run('залог NFT из «Моих NFT»', async () => {
              // Тот же вопрос цепочке, что и в конструкторе: выставленным
              // на продажу NFT владеет контракт площадки, и перевод он
              // отвергнет — молча и за деньги человека.
              const owner = await getNftOwner(card.nft);
              if (owner !== null && !sameAddress(owner, actions.myAddress)) {
                throw new Error(t('builder.not_owner_now'));
              }
              await actions.lockNft({ nftAddress: card.nft, dealAddress: card.deal! });
            })
          }
        >
          {busy ? '…' : t('builder.lock_gift')}
        </button>
        {err !== null && <span className="danger" style={{ fontSize: 10.5 }}>{err}</span>}
      </>
    );
  }

  // В контракте, покупателя нет. Отмена здесь возможна и безопасна:
  // обязательств ещё ни перед кем нет, вещь возвращается владельцу.
  return (
    <>
      <span className="dim" style={{ fontSize: 10.5, lineHeight: 1.3 }}>{t('mine.waiting_buyer')}</span>
      <button
        className="btn btn-ghost"
        style={{ padding: 9, fontSize: 11.5 }}
        disabled={busy}
        onClick={() => setConfirming(true)}
      >
        {busy ? '…' : t('deal.cancel_deal')}
      </button>
      {err !== null && <span className="danger" style={{ fontSize: 10.5 }}>{err}</span>}
      {confirming && (
        <Sheet
          title={t('confirm.cancel_deal')}
          confirmLabel={t('deal.cancel_deal')}
          danger
          busy={busy}
          onClose={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            void run('отмена оффера', () => actions.cancelDeal(card.deal!));
          }}
        >
          <span className="text">{t('confirm.cancel_deal_body')}</span>
        </Sheet>
      )}
    </>
  );
}

/**
 * Свои NFT: с чего начинается продажа.
 *
 * Раньше выставить свою вещь можно было только из пустого состояния доски
 * — карточки «запросов пока нет». Стоило появиться чужому запросу, и
 * единственный вход в конструктор исчезал с экрана: главное действие
 * продавца пропадало ровно тогда, когда сервис начинал работать.
 *
 * Показываем только допущенные коллекции и только то, чем человек владеет
 * прямо сейчас: выставленное на площадке принадлежит её контракту, а не
 * ему, и контракт такой залог отвергнет.
 */
function MyNfts({ onList, onEmpty }: { onList: (nft: string) => void; onEmpty: ReactNode }) {
  const { t } = useTranslation();
  const actions = useActions();
  const nameOf = useCollectionNames();
  const [pick, setPick] = useState<string | null>(null);

  const gifts = useQuery({
    queryKey: ['myNfts', actions.myAddress],
    queryFn: () => fetchOwnedNfts(actions.myAddress),
    enabled: actions.connected,
  });
  const registry = useQuery({ queryKey: ['registry'], queryFn: fetchRegistry, staleTime: 60_000 });
  /**
   * Свои сделки — чтобы не предложить выставить то, что уже выставлено.
   *
   * Оффер создаётся в два шага, и между ними NFT остаётся в кошельке:
   * сделка есть, а вещь ещё дома. Раздел показывал её как свободную и вёл
   * в конструктор — человек начинал вторую выкладку той же вещи вместо
   * того, чтобы закончить первую, а мастер развернул бы вторую сделку на
   * тот же NFT, и залог ушёл бы в одну из них наугад.
   */
  const deals = useQuery({
    queryKey: ['myDeals', actions.myAddress],
    queryFn: () => fetchWalletDeals(actions.myAddress),
    enabled: actions.connected,
  });

  if (!actions.connected) return <div className="card muted">{t('state.connect_wallet')}</div>;
  if (gifts.isLoading || registry.isLoading) {
    return <div className="skeleton" style={{ minHeight: 160 }} />;
  }
  /**
   * Не ответил кто-то из двоих — говорим об этом, а не показываем пустоту.
   *
   * Список вещей приходит из TON API прямо в браузер, список допущенных
   * коллекций — с нашего сервера, и любой из них может не ответить: у
   * первого лимиты на запросы, второй переживает выкаты. Оба отказа
   * сходились в один экран «выставлять нечего» — тот же самый, что видит
   * человек с пустым кошельком. Человек с подходящей вещью в руках читал,
   * что вещи у него нет, и считал раздел сломанным. Так и было.
   */
  if (gifts.isError || registry.isError) {
    return (
      <div className="card col">
        <strong className="title">{t('mine.load_failed_title')}</strong>
        <span className="text">{t('mine.load_failed')}</span>
        <button
          className="btn"
          onClick={() => {
            void gifts.refetch();
            void registry.refetch();
          }}
        >
          {t('state.retry')}
        </button>
      </div>
    );
  }

  const allowed = registry.data?.collections ?? [];

  // Свои сделки, в которых покупателя ещё нет. Состояние 0 — вещь дома,
  // ждёт передачи; 1 — уже в контракте, ждёт покупателя. Обе принадлежат
  // этому разделу: в «Сделках» им делать нечего, пока сделки как таковой
  // — с покупателем и графиком — не существует.
  const mineDeals = (deals.data?.deals ?? []).filter(
    (d) => sameAddress(d.seller, actions.myAddress) && (d.state === 0 || d.state === 1),
  );
  const fromDeals: MineCard[] = mineDeals.map((d) => ({
    nft: d.nftAddress,
    name: d.nftName,
    image: d.nftImage,
    collection: d.collection,
    kind: d.state === 0 ? 'awaiting_lock' : 'locked',
    deal: d.address,
  }));

  // Показываем только то, что здесь принимают: сделку по чужой коллекции
  // мастер не пропустит, и звать к ней покупателя значит вести в тупик.
  // Вещи с уже начатым оффером берём из сделки, а не из кошелька, — иначе
  // одна и та же вещь попала бы в список дважды.
  const free: MineCard[] = (gifts.data ?? [])
    .filter(
      (g) =>
        !g.onSale &&
        g.collection !== null &&
        allowed.some((c) => sameAddress(c.address, g.collection!)) &&
        !fromDeals.some((d) => sameAddress(d.nft, g.address)),
    )
    .map((g) => ({
      nft: g.address,
      name: g.name,
      image: g.image,
      collection: g.collection,
      kind: 'free' as const,
    }));

  // Выставленное — первым: это то, что человек оставил незаконченным или
  // чего ждёт, а свободное никуда не денется.
  const eligible = [...fromDeals, ...free];

  const onSale = (gifts.data ?? []).filter(
    (g) => g.onSale && g.collection !== null && allowed.some((c) => sameAddress(c.address, g.collection!)),
  ).length;

  if (eligible.length === 0) {
    // Пусто — не повод показывать пустоту: человеку без подходящей вещи
    // полезнее увидеть, что у таких, как он, просят.
    return (
      <>
        <div className="card col">
          <strong className="title">{t('mine.nothing_title')}</strong>
          <span className="text">
            {onSale > 0 ? t('mine.all_on_sale', { count: onSale }) : t('mine.nothing_hint')}
          </span>
        </div>
        {onEmpty}
      </>
    );
  }

  // Коллекции только те, в которых у человека что-то есть: фильтр по
  // пустому — это фильтр, который нечего фильтровать.
  const collections = [
    ...new Set(eligible.map((g) => g.collection).filter((c): c is string => c !== null)),
  ];
  const shown =
    pick === null
      ? eligible
      : eligible.filter((g) => g.collection !== null && sameAddress(g.collection, pick));

  return (
    <>
      <span className="muted" style={{ fontSize: 12 }}>{t('mine.lead')}</span>

      {collections.length > 1 && (
        <div className="chips">
          <button aria-selected={pick === null} onClick={() => setPick(null)}>
            {t('market.all_collections')}
          </button>
          {collections.map((c) => (
            <button
              key={c}
              aria-selected={pick !== null && sameAddress(c, pick)}
              onClick={() => setPick(c)}
            >
              {nameOf(c).name ?? shortAddress(c)}
            </button>
          ))}
        </div>
      )}

      <div className="grid2">
        {shown.map((item) => (
          <div key={item.nft} className="card col" style={{ padding: 10, gap: 9, minWidth: 0 }}>
            <GiftArt image={item.image} size={88} radius={11} full />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.name ?? shortAddress(item.nft)}
              </div>
              <div className="label" style={{ marginTop: 3 }}>
                {nameOf(item.collection ?? '').name ?? ''}
              </div>
            </div>
            <CardAction card={item} onList={() => onList(item.nft)} />
          </div>
        ))}
      </div>

      {onSale > 0 && (
        <span className="muted" style={{ fontSize: 12 }}>{t('mine.also_on_sale', { count: onSale })}</span>
      )}
    </>
  );
}

/**
 * Три взгляда на одно: что у меня есть, что просят у меня, что прошу я.
 *
 * Прежнее деление было по ролям — «запросы», «мои запросы», «мои
 * предложения», — и человек не понимал, где его собственное действие: то,
 * что он отправил как покупатель, лежало отдельно от того, что он
 * отправил как продавец, хотя это одно и то же — отправленное им.
 * Направление письма понятнее роли: мне написали или написал я.
 */
function Segments({
  side,
  setSide,
}: {
  side: 'mine' | 'in' | 'out';
  setSide: (s: 'mine' | 'in' | 'out') => void;
}) {
  const { t } = useTranslation();
  const items = [
    ['mine', t('mine.my_nfts')],
    ['in', t('mine.incoming')],
    ['out', t('mine.outgoing')],
  ] as const;

  return (
    <div className="segmented" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
      {items.map(([name, label]) => (
        <button key={name} aria-selected={side === name} onClick={() => setSide(name)}>
          {label}
        </button>
      ))}
    </div>
  );
}

const REQUEST_STATUS: Record<RequestDto['status'], string> = {
  open: 'request.status_open',
  countered: 'request.status_countered',
  accepted: 'request.status_accepted',
  declined: 'request.status_declined',
  cancelled: 'request.status_cancelled',
};

const COUNTER_STATUS: Record<CounterDto['status'], string> = {
  open: 'counter.status_open',
  accepted: 'counter.status_accepted',
  declined: 'counter.status_declined',
  expired: 'counter.status_expired',
};

/**
 * Встречное предложение глазами покупателя.
 *
 * Главное здесь — не цена, а что будет при срыве: уровень возврата назначил
 * продавец, покупатель видит его впервые и соглашается именно на него.
 */
function CounterCard({
  counter,
  request,
  actionable,
}: {
  counter: CounterDto;
  request: RequestDto | undefined;
  actionable: boolean;
}) {
  const { t } = useTranslation();
  const { money, duration } = useFmt();
  const actions = useActions();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const now = Math.floor(Date.now() / 1000);

  const body = { initData: getInitData(), address: actions.myAddress };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['myRequests'] });
    void qc.invalidateQueries({ queryKey: ['myCounters'] });
  };

  const accept = useMutation({ mutationFn: () => acceptCounter(counter.id, body), onSuccess: refresh });
  const decline = useMutation({ mutationFn: () => declineCounter(counter.id, body), onSuccess: refresh });

  const installment = (BigInt(counter.totalPrice) - BigInt(counter.upfront)) / BigInt(counter.nPayments);
  const live = counter.status === 'open' && counter.expiresAt > now;
  // Какую вещь отдаёт продавец. У ответов, написанных до появления поля,
  // её нет — тогда и показывать нечего, врать не будем.
  const item =
    counter.itemName ?? (counter.nftAddress === null ? null : shortAddress(counter.nftAddress));
  const asked = request === undefined ? null : BigInt(request.totalPrice);
  const diff = asked === null ? 0n : BigInt(counter.totalPrice) - asked;

  if (accept.isSuccess) {
    return (
      <div className="card col">
        <strong>{t('counter.accepted_title')}</strong>
        <span className="muted" style={{ fontSize: 13 }}>{t('counter.accepted_hint')}</span>
      </div>
    );
  }

  return (
    <div className="card col" style={{ borderColor: live ? 'var(--line)' : 'transparent' }}>
      <div className="row">
        <span className="label">{t('counter.they_offer')}</span>
        <span className="muted" style={{ fontSize: 12 }}>{t(COUNTER_STATUS[counter.status])}</span>
      </div>

      {/* Что именно предлагают. Раньше продавец отвечал условиями, не
          называя вещь, и покупатель соглашался вслепую: цена, график и
          возврат были известны, а предмет — нет. */}
      {item !== null && (
        <div className="row">
          <span className="muted">{t('counter.item')}</span>
          <span className="num" style={{ fontSize: 12.5 }}>{item}</span>
        </div>
      )}

      <div className="row">
        <span className="muted">{t('counter.total')}</span>
        <span className="num">
          <Money value={counter.totalPrice} />
          {diff !== 0n && (
            <span className={diff > 0n ? 'danger' : 'muted'} style={{ fontSize: 12 }}>
              {' '}({diff > 0n ? '+' : '−'}{money(diff > 0n ? diff : -diff)})
            </span>
          )}
        </span>
      </div>
      <div className="row">
        <span className="muted">{t('counter.down')}</span>
        <span className="num"><Money value={counter.upfront} /></span>
      </div>
      <div className="row">
        <span className="muted">{t('counter.payments')}</span>
        <span className="num">
          {counter.nPayments} × {money(installment)} · {t('board.every')} {duration(counter.period)}
        </span>
      </div>

      {/* Последствия срыва — суммой и сроком, а не одним процентом. */}
      <div className="row">
        <span className="muted">{t('counter.refund')}</span>
        <span className="num">{String(refundPercentOf(counter.refundTier))}%</span>
      </div>
      <div className="row">
        <span className="muted">{t('counter.grace')}</span>
        <span className="num">{duration(counter.graceSec)}</span>
      </div>
      <div className="row">
        <span className="muted">{t('counter.deferrals')}</span>
        <span className="num">
          {counter.maxDeferrals === 0
            ? t('builder.none')
            : `${counter.maxDeferrals} · ${money(counter.deferFee)}`}
        </span>
      </div>

      {live && (
        <div className="row">
          <span className="muted">{t('counter.expires_in')}</span>
          <span className="num">{duration(counter.expiresAt - now)}</span>
        </div>
      )}

      {(accept.error ?? decline.error) != null && (
        <div className="danger" style={{ fontSize: 13 }}>
          {errorText(accept.error ?? decline.error)}
        </div>
      )}

      {live && actionable && (
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn"
            disabled={accept.isPending || decline.isPending}
            onClick={() => accept.mutate()}
          >
            {accept.isPending ? '…' : t('counter.accept')}
          </button>
          <button
            className="btn btn-ghost"
            disabled={accept.isPending || decline.isPending}
            onClick={() => decline.mutate()}
          >
            {t('counter.decline')}
          </button>
        </div>
      )}

      {live && !actionable && (
        <button className="btn btn-ghost" disabled={decline.isPending} onClick={() => decline.mutate()}>
          {t('counter.withdraw')}
        </button>
      )}
    </div>
  );
}

function RequestCard({ request, counters }: { request: RequestDto; counters: CounterDto[] }) {
  const { t } = useTranslation();
  const { money, duration } = useFmt();
  const nameOf = useCollectionNames();
  const actions = useActions();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const now = Math.floor(Date.now() / 1000);

  const withdraw = useMutation({
    mutationFn: () => cancelRequest(request.id, { initData: getInitData(), address: actions.myAddress }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['myRequests'] }),
  });

  const live = (request.status === 'open' || request.status === 'countered') && request.expiresAt > now;
  const installment = (BigInt(request.totalPrice) - BigInt(request.upfront)) / BigInt(request.nPayments);

  return (
    <div className="col" style={{ gap: 8 }}>
      <div className="card col">
        <strong style={{ fontSize: 14 }}>
          {/* Именной оффер — про вещь, оффер на коллекцию — про коллекцию.
              Показывать в обоих случаях название коллекции значило прятать
              главное: покупатель с тремя офферами не отличал их друг от
              друга. */}
          {request.scope === 'item'
            ? request.itemName ?? shortAddress(request.nftAddress ?? '')
            : nameOf(request.collection).name ?? shortAddress(request.collection)}
        </strong>
        <div className="row">
          <span className="label">
            {request.scope === 'item' ? t('request.scope_item') : t('request.scope_collection')}
          </span>
          {request.scope === 'item' && (
            <span className="dim" style={{ fontSize: 11 }}>
              {nameOf(request.collection).name ?? shortAddress(request.collection)}
            </span>
          )}
        </div>
        <div className="row">
          <span className="label">{t('counter.asked')}</span>
          <span className="muted" style={{ fontSize: 12 }}>
            {live ? t(REQUEST_STATUS[request.status]) : t('request.expired')}
          </span>
        </div>
        <div className="big"><Money value={request.totalPrice} /></div>
        <div className="row">
          <span className="muted">{t('counter.down')}</span>
          <span className="num"><Money value={request.upfront} /></span>
        </div>
        <div className="row">
          <span className="muted">{t('counter.payments')}</span>
          <span className="num">{request.nPayments} × {money(installment)}</span>
        </div>
        {live && (
          <div className="row">
            <span className="muted">{t('board.request_expires')}</span>
            <span className="num">{duration(request.expiresAt - now)}</span>
          </div>
        )}
        <div className="row">
          <span className="muted">{t('request.answers')}</span>
          <span className="num">{counters.length}</span>
        </div>

        {withdraw.error !== null && withdraw.error !== undefined && (
          <div className="danger" style={{ fontSize: 13 }}>{errorText(withdraw.error)}</div>
        )}

        {live && (
          <button className="btn btn-ghost" disabled={withdraw.isPending} onClick={() => withdraw.mutate()}>
            {t('request.withdraw')}
          </button>
        )}
      </div>

      {counters.length === 0 && live && (
        <div className="card muted" style={{ fontSize: 13 }}>{t('counter.none')}</div>
      )}

      {counters.map((c) => (
        <CounterCard key={c.id} counter={c} request={request} actionable />
      ))}
    </div>
  );
}

/**
 * Торг целиком: чужой спрос, свои запросы, свои предложения.
 *
 * Один экран с переключателем, а не три пункта меню: это три взгляда на
 * один процесс, и человек за время сделки бывает на обеих его сторонах.
 */
export function Requests({ onBuilder }: { onBuilder: (nft?: string) => void }) {
  const { t } = useTranslation();
  const actions = useActions();
  // По умолчанию — свои вещи: главное действие продавца начинается с них.
  const [side, setSide] = useState<'mine' | 'in' | 'out'>('mine');

  const mine = useQuery({
    queryKey: ['myRequests', actions.myAddress],
    queryFn: () => fetchMyRequests(actions.myAddress),
    enabled: actions.connected,
  });
  const sent = useQuery({
    queryKey: ['myCounters', actions.myAddress],
    queryFn: () => fetchMyCounters(actions.myAddress),
    enabled: actions.connected && side === 'out',
  });

  // Доска — единственная часть, которую видно без кошелька: спрос открыт
  // всем, а вот свои запросы без адреса показать не из чего.
  // Все три вида живут внутри одного .screen: он и есть прокручиваемая
  // область. Экран без него не растягивается, и нижнее меню уезжает вверх —
  // ровно это и случилось с доской.
  if (side === 'mine') {
    return (
      <div className="screen">
        <Segments side={side} setSide={setSide} />
        <MyNfts onList={onBuilder} onEmpty={<Board onBuilder={onBuilder} />} />
      </div>
    );
  }

  if (side === 'in') {
    return (
      <div className="screen">
        <Segments side={side} setSide={setSide} />
        <Board onBuilder={onBuilder} />
      </div>
    );
  }

  if (!actions.connected) {
    return (
      <div className="screen">
        <Segments side={side} setSide={setSide} />
        <div className="card muted">{t('state.connect_wallet')}</div>
      </div>
    );
  }

  // Исходящее — это и мои запросы, и мои предложения чужим: отправленное
  // мной, независимо от того, кем я был в тот момент.
  const loading = mine.isLoading || sent.isLoading;
  const nothingSent =
    !loading &&
    (mine.data?.requests.length ?? 0) === 0 &&
    (sent.data?.counters.length ?? 0) === 0;

  return (
    <div className="screen">
      <Segments side={side} setSide={setSide} />

      {loading && <div className="skeleton" style={{ minHeight: 140 }} />}

      {nothingSent && <div className="card muted">{t('request.none')}</div>}

      {(mine.data?.requests.length ?? 0) > 0 && (
        <div className="label label-lg">{t('request.my')}</div>
      )}

      {mine.data?.requests.map((r) => (
        <RequestCard
          key={r.id}
          request={r}
          counters={(mine.data?.counters ?? []).filter((c) => c.requestId === r.id)}
        />
      ))}

      {(sent.data?.counters.length ?? 0) > 0 && (
        <div className="label label-lg">{t('counter.my_offers')}</div>
      )}

      {
        sent.data?.counters.map((c) => (
          <CounterCard
            key={c.id}
            counter={c}
            request={sent.data?.requests.find((r) => r.id === c.requestId)}
            actionable={false}
          />
        ))}
    </div>
  );
}
